import { parseSystemColumnFieldId } from '@doss/common-all'
import { FormatPreset } from '@doss/prisma/types'
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from '@radix-ui/react-popover'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { PiFunction } from 'react-icons/pi'
import { Button } from '~/@/components/ui/button'
import {
  DataType,
  SystemGeneratedTableColumnType,
  TableColumnFilterInput,
  useAggregateTableColumnLazyQuery,
} from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getHasChangedFilters } from '~/common/utils/table'
import { LoadingSpinner } from '../ui/atoms/loading-spinner/LoadingSpinner'
import {
  formatMetricValue,
  getAbbreviatedMetricLabel,
  getMetricOptions,
  METRIC_TO_AGGREGATION_OPERATION,
  MetricType,
} from './aggregation-functions'
import {
  DossGridFilterModel,
  filterOperatorToBooleanOperator,
  generateFilterInput,
  generateSystemColumnFilterInput,
} from './filters'
import { DetailedTableColumn, DetailedTableTab } from './types'

// Marker ID for the aggregation row - used to detect in renderCell
export const AGGREGATION_ROW_ID = '__aggregation_row__' as const

/**
 * Type guard to check if a row is the aggregation row.
 * Use this instead of direct id comparison for type safety.
 */
export const isAggregationRow = (row: { id: string }): boolean => row.id === AGGREGATION_ROW_ID

// Context for triggering aggregation refresh without re-rendering the entire table
// When the refresh key changes, only components consuming this context re-render
const AggregationRefreshContext = createContext<number>(0)

/**
 * Provider component that wraps the DataGrid to provide aggregation refresh functionality.
 * Only AggregationCell components (which consume this context) will re-render when
 * refreshKey changes, not the entire table.
 */
export const AggregationRefreshProvider = ({
  children,
  refreshKey,
}: {
  children: React.ReactNode
  refreshKey: number
}) => {
  return (
    <AggregationRefreshContext.Provider value={refreshKey}>
      {children}
    </AggregationRefreshContext.Provider>
  )
}

/**
 * Hook to get the current aggregation refresh key from context.
 * Components using this hook will re-render when the refresh key changes.
 */
const useAggregationRefreshKey = () => useContext(AggregationRefreshContext)

interface AggregationCellProps {
  columnId: string
  columnType: DataType
  tableColumns: DetailedTableColumn[]
  tableTabId?: string
  selectedTab?: DetailedTableTab
  filterModel: DossGridFilterModel
  /** Column format preset for number formatting (currency, percent, etc.) */
  formatPreset?: FormatPreset | null
  /** Column format options that pair with the preset */
  formatOptions?: unknown
  /** Current selected metric (lifted state from parent) */
  selectedMetric?: MetricType
  /** Callback to update selected metric (lifted state from parent) */
  onMetricChange?: (metric: MetricType) => void
  /**
   * For child tables: the parent row ID to scope aggregation to children of that parent.
   */
  parentRowId?: string
  /**
   * For backlink tables: the column containing the link to the source table.
   * Must be provided together with backlinkRowId.
   */
  backlinkColumnId?: string
  /**
   * For backlink tables: the source row ID to scope aggregation to rows linked to that row.
   * Must be provided together with backlinkColumnId.
   */
  backlinkRowId?: string
  /**
   * Optional search string to filter aggregation results.
   */
  searchString?: string
}

/**
 * Self-contained aggregation cell component that manages its own state.
 * Renders a dropdown to select aggregation type and fetches/displays the result.
 */
export const AggregationCell = ({
  columnId,
  columnType,
  tableColumns,
  tableTabId,
  selectedTab,
  filterModel,
  formatPreset,
  formatOptions,
  selectedMetric: selectedMetricProp,
  onMetricChange,
  parentRowId,
  backlinkColumnId,
  backlinkRowId,
  searchString,
}: AggregationCellProps) => {
  const { t } = useTypedTranslation()
  const { locale } = useGlobalContext()
  // Get refresh key from context - only this component re-renders when it changes
  const refreshKey = useAggregationRefreshKey()
  // Use lifted state if provided, otherwise use local state for backwards compatibility
  const [localSelectedMetric, setLocalSelectedMetric] = useState<MetricType>('none')
  const selectedMetric = selectedMetricProp ?? localSelectedMetric
  const setSelectedMetric = onMetricChange ?? setLocalSelectedMetric
  const [aggregationValue, setAggregationValue] = useState<number | null | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const [fetchAggregation] = useAggregateTableColumnLazyQuery()

  // Determine if filters have changed from the saved table tab filters
  const hasChangedFilters = useMemo(() => {
    if (!selectedTab) {
      return false
    }
    return getHasChangedFilters(tableColumns, selectedTab, filterModel)
  }, [tableColumns, selectedTab, filterModel])

  // Convert current filter model to TableColumnFilterInput[] if filters have changed
  const currentFilters = useMemo((): TableColumnFilterInput[] | undefined => {
    if (!hasChangedFilters) {
      return undefined
    }

    const filters: TableColumnFilterInput[] = []
    filterModel.items.forEach((filterItem) => {
      const tableColumn = tableColumns.find((col) => col.id === filterItem.field)
      if (tableColumn) {
        filters.push(generateFilterInput(tableColumn, filterItem))
      } else {
        const systemColumnType = parseSystemColumnFieldId(filterItem.field)
        if (systemColumnType) {
          filters.push(
            generateSystemColumnFilterInput(
              systemColumnType as SystemGeneratedTableColumnType,
              filterItem,
            ),
          )
        }
      }
    })
    return filters
  }, [hasChangedFilters, filterModel, tableColumns])

  // Fetch aggregation for a given metric type
  const fetchAggregationValue = useCallback(
    async (metricType: Exclude<MetricType, 'none'>) => {
      setIsLoading(true)

      try {
        const result = await fetchAggregation({
          variables: {
            input: {
              columnId,
              ...(hasChangedFilters
                ? {
                    filters: currentFilters,
                    filterBooleanOperator: filterOperatorToBooleanOperator(
                      filterModel.logicOperator,
                    ),
                  }
                : { tableTabId }),
              operation: METRIC_TO_AGGREGATION_OPERATION[metricType],
              // For child tables: scope aggregation to children of this parent row
              parentRowId,
              // For backlink tables: scope aggregation to rows linked to this source row
              backlinkColumnId,
              backlinkRowId,
              // Optional search string filter
              searchString: searchString || undefined,
            },
          },
          // Always fetch fresh data - especially important for refresh button functionality
          fetchPolicy: 'network-only',
        })

        setAggregationValue(result.data?.aggregateTableColumn.value)
      } catch (error) {
        console.error('Error fetching aggregation:', error)
        setAggregationValue(null)
      } finally {
        setIsLoading(false)
      }
    },
    [
      columnId,
      fetchAggregation,
      hasChangedFilters,
      currentFilters,
      filterModel.logicOperator,
      tableTabId,
      parentRowId,
      backlinkColumnId,
      backlinkRowId,
      searchString,
    ],
  )

  const handleMetricChange = (metricType: MetricType) => {
    setSelectedMetric(metricType)

    if (metricType === 'none') {
      setAggregationValue(undefined)
      return
    }

    fetchAggregationValue(metricType)
  }

  // Re-fetch when filters or tab changes (if a metric is selected)
  const prevFilterModel = useRef(filterModel)
  const prevTableTabId = useRef(tableTabId)

  useEffect(() => {
    const filterChanged = prevFilterModel.current !== filterModel
    const tabChanged = prevTableTabId.current !== tableTabId

    if ((filterChanged || tabChanged) && selectedMetric !== 'none') {
      fetchAggregationValue(selectedMetric)
    }

    prevFilterModel.current = filterModel
    prevTableTabId.current = tableTabId
  }, [filterModel, tableTabId, selectedMetric, fetchAggregationValue])

  // Fetch on mount if a metric is already selected (handles component recreation/remount)
  // This is needed because when columns useMemo re-runs, MUI may recreate cell components
  const hasMountedRef = useRef(false)
  useEffect(() => {
    if (!hasMountedRef.current && selectedMetric !== 'none') {
      fetchAggregationValue(selectedMetric)
    }
    hasMountedRef.current = true
  }, [selectedMetric, fetchAggregationValue])

  // Re-fetch when refreshKey changes (handles re-render without remount case)
  const prevRefreshKey = useRef(refreshKey)
  useEffect(() => {
    // Only fetch if refreshKey changed AND we have a metric selected
    // Skip on first render (mount is handled above)
    if (
      hasMountedRef.current &&
      prevRefreshKey.current !== refreshKey &&
      selectedMetric !== 'none'
    ) {
      fetchAggregationValue(selectedMetric)
    }
    prevRefreshKey.current = refreshKey
  }, [refreshKey, selectedMetric, fetchAggregationValue])

  const metricOptions = getMetricOptions(columnType, t)
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div
      className="flex size-full items-center gap-1 overflow-hidden px-1"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Ghost-style button trigger */}
      <Popover onOpenChange={setIsOpen} open={isOpen}>
        <PopoverTrigger asChild>
          <Button className="h-auto shrink-0 px-1.5 py-0.5" type="button" variant="ghost">
            {selectedMetric === 'none' ? (
              <PiFunction className="size-3.5" />
            ) : (
              <span className="text-xs font-medium">
                {getAbbreviatedMetricLabel(selectedMetric)}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent
            align="start"
            className="z-[9999] rounded-sm border bg-white p-1 shadow-md"
            side="top"
            sideOffset={4}
          >
            <div className="flex flex-col">
              {metricOptions.map((option) => (
                <button
                  key={option.value}
                  className={`rounded px-2 py-1 text-left text-xs hover:bg-gray-2 ${
                    option.value === selectedMetric ? 'bg-gray-2 font-medium' : ''
                  }`}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleMetricChange(option.value)
                    setIsOpen(false)
                  }}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </PopoverContent>
        </PopoverPortal>
      </Popover>
      {/* Aggregation value - takes remaining space, right-aligned */}
      {selectedMetric !== 'none' && (
        <div className="min-w-0 flex-1 truncate text-right text-xs font-medium text-gray-10">
          {isLoading && <LoadingSpinner type="navigation" />}
          {!isLoading &&
            aggregationValue !== undefined &&
            (() => {
              const formattedValue = formatMetricValue(
                aggregationValue,
                selectedMetric,
                locale,
                formatPreset,
                formatOptions,
              )
              return <span title={formattedValue}>{formattedValue}</span>
            })()}
        </div>
      )}
    </div>
  )
}
